import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Search, Wallet as WalletIcon } from 'lucide-react'
import { adjustWallet, listWallets, walletLedger, type AdminWallet } from '@/api/billingAdmin'
import type { LedgerEntry } from '@/api/billing'
import { AdminModal, Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from '../adminUi'

const OWNER_TYPES = [
  { value: '', label: 'All wallets' },
  { value: 'user', label: 'Users' },
  { value: 'tenant', label: 'Organizations' },
  { value: 'project', label: 'Applications' },
]

/**
 * Every wallet on the instance, and the one manual lever an operator needs:
 * a signed adjustment. Adjustments are ordinary ledger entries, so the ledger
 * stays a complete explanation of every balance.
 */
export default function WalletsPanel() {
  const [wallets, setWallets] = useState<AdminWallet[]>([])
  const [ownerType, setOwnerType] = useState('')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [adjusting, setAdjusting] = useState<AdminWallet | null>(null)
  const [viewing, setViewing] = useState<AdminWallet | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setWallets((await listWallets({
        owner_type: ownerType || undefined,
        q: query.trim() || undefined,
      })).wallets)
    } catch (e) {
      toast.error(errText(e, 'Could not load wallets'))
    } finally {
      setLoading(false)
    }
  }, [ownerType, query])

  useEffect(() => { void load() }, [load])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <InfoBox>
        A wallet belongs to a user, an organization or an application. An
        adjustment here writes a normal ledger entry — it is visible to the owner
        in their credit history, exactly like a purchase or a grant.
      </InfoBox>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <select value={ownerType} onChange={(e) => setOwnerType(e.target.value)}>
          {OWNER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)',
          }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void load() }}
            placeholder="Search by owner…"
            style={{ width: '100%', paddingLeft: 28 }}
          />
        </div>
        <button className="btn btn-secondary" onClick={load}>Search</button>
      </div>

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr><Th>Type</Th><Th>Owner</Th><Th right>Balance</Th><Th /></tr>
          </thead>
          <tbody>
            {wallets.map((w) => (
              <tr key={w.id}>
                <Td><span style={chipStyle}>{w.owner_type}</span></Td>
                <Td>
                  <div>{w.label}</div>
                  {w.label !== w.owner_ref && (
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      {w.owner_ref}
                    </div>
                  )}
                </Td>
                <Td right style={{
                  fontVariantNumeric: 'tabular-nums',
                  color: w.balance < 0 ? 'var(--color-danger, #d64545)' : undefined,
                }}>
                  {w.currency ? w.display : `${w.balance.toLocaleString()} credits`}
                </Td>
                <Td right>
                  <button className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem', marginRight: 4 }}
                          onClick={() => setViewing(w)}>History</button>
                  <button className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem' }}
                          onClick={() => setAdjusting(w)}>Adjust</button>
                </Td>
              </tr>
            ))}
            {!wallets.length && (
              <tr><Td style={{ color: 'var(--color-text-secondary)' }}>No wallets match.</Td></tr>
            )}
          </tbody>
        </table>
      )}

      {adjusting && (
        <AdjustModal wallet={adjusting} onClose={() => setAdjusting(null)}
                     onDone={async () => { setAdjusting(null); await load() }} />
      )}
      {viewing && <LedgerModal wallet={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}

function AdjustModal({ wallet, onClose, onDone }: {
  wallet: AdminWallet; onClose: () => void; onDone: () => void | Promise<void>
}) {
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const apply = async () => {
    const value = parseFloat(amount)
    if (!Number.isFinite(value) || value === 0) {
      toast.error('Enter a non-zero amount')
      return
    }
    setSaving(true)
    try {
      // Money wallets store minor units; the admin types major (₹/$).
      const raw = wallet.currency ? Math.round(value * 100) : value
      await adjustWallet(wallet.id, raw, description.trim() || undefined)
      toast.success('Adjustment applied')
      await onDone()
    } catch (e) {
      toast.error(errText(e, 'Could not adjust the wallet'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminModal title={`Adjust ${wallet.label}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.9375rem' }}>
          <WalletIcon size={16} style={{ color: 'var(--color-accent)' }} />
          Current balance: <strong>{wallet.currency ? wallet.display : `${wallet.balance.toLocaleString()} credits`}</strong>
        </div>
        <Field label={wallet.currency ? `Amount (${wallet.currency})` : 'Amount (credits)'}
               hint={wallet.currency
                 ? 'Positive adds funds, negative removes them.'
                 : 'Positive adds credits, negative removes them.'}>
          <input type="number" step="0.01" value={amount}
                 onChange={(e) => setAmount(e.target.value)}
                 placeholder="e.g. 500 or -250" style={{ width: '100%' }} />
        </Field>
        <Field label="Reason" hint="Shown to the owner in their credit history.">
          <input value={description} onChange={(e) => setDescription(e.target.value)}
                 placeholder="Goodwill credit for the March outage" style={{ width: '100%' }} />
        </Field>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={apply} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : 'Apply adjustment'}
          </button>
        </div>
      </div>
    </AdminModal>
  )
}

function LedgerModal({ wallet, onClose }: { wallet: AdminWallet; onClose: () => void }) {
  const [entries, setEntries] = useState<LedgerEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    walletLedger(wallet.id, { limit: 100 })
      .then((d) => setEntries(d.entries))
      .catch(() => toast.error('Could not load the ledger'))
      .finally(() => setLoading(false))
  }, [wallet.id])

  return (
    <AdminModal title={`Ledger — ${wallet.label}`} onClose={onClose} wide>
      {loading ? <Loader2 className="spin" /> : (
        <table style={tableStyle}>
          <thead>
            <tr><Th>When</Th><Th>Type</Th><Th>Description</Th><Th right>Amount</Th><Th right>Balance</Th></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <Td>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</Td>
                <Td><span style={chipStyle}>{e.entry_type}</span></Td>
                <Td>{e.description || '—'}</Td>
                <Td right style={{ color: e.amount >= 0 ? 'var(--color-success, #2e9e5b)' : undefined }}>
                  {e.amount >= 0 ? '+' : ''}{e.amount.toLocaleString()}
                </Td>
                <Td right>{e.balance_after.toLocaleString()}</Td>
              </tr>
            ))}
            {!entries.length && (
              <tr><Td style={{ color: 'var(--color-text-secondary)' }}>No entries.</Td></tr>
            )}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </AdminModal>
  )
}
